import type { Action } from '@0xtorch/core'
import { createEvmActionSource, isHex } from '@0xtorch/evm'
import { createSolanaActionSource } from '@0xtorch/solana'
import { ApiSecretRequiredException } from '../../errors'
import type { FetchApiData } from '../../types'
import { networkEvmChainIds, solanaNetwork } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/v5/asset/deposit/query-record',
    query: {
      limit: config.limit.toString(),
      ...(from ? { startTime: from.toString() } : {}),
      ...(to ? { endTime: to.toString() } : {}),
    },
    responseSchema,
  })

  // 昇順 sort by successAt
  const sortedDeposits = response.result.rows.sort(
    (a, b) =>
      Number.parseInt(a.successAt, 10) - Number.parseInt(b.successAt, 10),
  )

  // データ処理
  let stopped = false
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of sortedDeposits) {
    // Skip deposits with status 4
    if (item.status === 4) {
      continue
    }

    // Stop processing if status is 0, 1, 2, or 10011
    if (
      item.status === 0 ||
      item.status === 1 ||
      item.status === 2 ||
      item.status === 10011
    ) {
      stopped = true
      break
    }

    const timestamp = Number.parseInt(item.successAt, 10)

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? timestamp : Math.min(fetchedFrom, timestamp)
    fetchedTo =
      fetchedTo === undefined ? timestamp : Math.max(fetchedTo, timestamp)

    // Create EVM source if applicable
    if (item.chain && item.txID) {
      const evmChainId = networkEvmChainIds[item.chain]
      if (evmChainId && isHex(item.txID)) {
        sources.push(
          createEvmActionSource({
            chainId: evmChainId,
            transactionHash: item.txID,
          }),
        )
      }
    }

    // Create Solana source if applicable
    if (item.chain === solanaNetwork && item.txID) {
      sources.push(createSolanaActionSource({ signature: item.txID }))
    }
  }

  return {
    actions,
    count: response.result.rows.length,
    from: fetchedFrom,
    sources,
    stopped,
    to: fetchedTo,
  }
}
